import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Auth0User, BaseRoleAccess, User } from '@app/models';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from '@env/environment';
import { jwtDecode } from 'jwt-decode';
import { Observable, catchError, filter, map, of, shareReplay, switchMap, tap } from 'rxjs';

import { QbService } from './qb.service';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  get isVendor(): boolean {
    return this.user?.isVendor && !this.user?.isAgency && !this.user?.isQbat;
  }

  // Colbi users are only agency users if they are not qbat users.
  get isAgency(): boolean {
    return (this.user?.isAgency || this.user?.isColbi) && !this.user?.isQbat;
  }

  get isQbat(): boolean {
    return this.user?.isQbat;
  }

  get isColbi(): boolean {
    return this.user?.isColbi;
  }

  private _loggedInUser: User;
  get user(): User {
    return this._loggedInUser;
  }
  set user(value: User) {
    this._loggedInUser = value;
  }

  checkAuth$: Observable<boolean>;

  // private _decodedToken = this.auth.getAccessTokenSilently().pipe(map(token => jwtDecode(token)));
  private _decodedToken = this.auth.isAuthenticated$.pipe(
    switchMap(isAuthenticated => (isAuthenticated ? this.auth.getAccessTokenSilently() : of(null))),
    filter((token): token is string => token !== null),
    map(token => jwtDecode(token)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  permissions = this._decodedToken.pipe(
    map(decoded => decoded[environment.auth0Audience + '/claims/permissions'] || decoded['permissions'] || []),
    tap(val => (this._storedPermissions = val)),
    catchError(() => {
      this._storedPermissions = [];
      return of([]);
    }),
    shareReplay(1)
  );
  roles = this._decodedToken.pipe(
    map(decoded => decoded[environment.auth0Audience + '/claims/roles'] || []),
    tap(val => (this._storedRoles = val)),
    catchError(() => {
      this._storedRoles = [];
      return of([]);
    }),
    shareReplay(1)
  );

  private _storedPermissions: string[];
  private _storedRoles: string[];

  constructor(
    private auth: AuthService,
    private router: Router,
    private qb: QbService
  ) {
    this.qb.loading = true;
    // this.checkAuth$ = this.auth.isAuthenticated$.pipe(
    //   switchMap(val => {
    //     return val ? of('true') : this.auth.getAccessTokenSilently();
    //   }),
    //   map(val => {
    //     if (!val) {
    //       // If we're not logged in, we most likely will not be doing anything further,
    //       // so we don't need to continue the loading icon.
    //       // NOTE: Dangerous if we use this.checkAuth$ for some reason and then we take the (negative) result of that to go do something else.
    //       // keep an eye out for this if global loading is not working properly?
    //       this.qb.loading = false;
    //     }
    //     return !!val;
    //   }),
    //   catchError(err => {
    //     if (err.error == 'login_required') {
    //       return of(null);
    //     }
    //     throw err;
    //   })
    // );

    this.checkAuth$ = this.auth.isAuthenticated$.pipe(
      tap(isAuthenticated => {
        if (!isAuthenticated) {
          this.qb.loading = false;
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    // Streams true if user has already been logged in to this domain on this browser.
    // Streams false if user has been logged out on this browser but logged in on another domain.
    this.checkAuth$.pipe(switchMap(isAuthenticated => (isAuthenticated ? this.auth.user$ : of(null)))).subscribe({
      next: (val: Auth0User) => {
        this.setUserLoggedIn(val);
      },
      error: (err: any) => {
        console.error('Error checking auth:', err);
        this.qb.loading = false;
      },
    });

    this.auth.error$.subscribe(e => {
      const error: {
        error: string;
        error_description: string;
        message: string;
      } = e as any;

      if (!environment.production) {
        console.log('Error from Auth0:');
        console.log(error);
      }

      const errorMessages: { [key: string]: string } = {
        'user is blocked': 'Your account has been deactivated. Please contact your administrator.',
        cannot_retrieve_user: 'There was an error with the login service. Please contact Colbi Support.',
        user_not_found: 'This user could not be found. Please contact Colbi Support.',
        lausd_user_not_found:
          'This user has not been added to QualityBidders to act on behalf of LAUSD. Please contact your administrator.',
        'Script execution time exceeded': 'Logging in is taking too long. Refreshing the page to try again.',
        Timeout: 'Logging in is taking too long. Refreshing the page to try again.',
        'Invalid authorization code': 'Error logging you in. Please try again.',
        no_agency:
          'Your account is no longer linked to an Agency. If you need your account reactivated, please contact your Agency directly.',
      };

      switch (error.error_description) {
        case 'Login required':
          break;
        case 'email_unverified':
          this.logOut('/verify', 'Please verify your email before logging in');
          break;
        default:
          if (errorMessages[error.error_description]) {
            this.logOut('/', errorMessages[error.error_description]);
          } else {
            console.error('Unknown Authorization Error:');
            console.error(e);
          }
      }
    });
  }

  /**
   *
   * @param target - Target path the app gets routed to after handling the callback.
   */
  login(target?: string) {
    this.qb.loading = true;
    this.auth.loginWithRedirect({
      appState: {
        returnTo: environment.auth0CallbackUrl,
        target: target ?? window.location.pathname,
      },
    });
  }

  /**
   *
   * @param returnTo Return to this relative URL. (starts with '/')
   * @param message Display this message when we return to our target URL.
   * @param logoutParams Overwrites returnTo and Message if necessary.
   */
  logOut(returnTo?: string, message?: string, logoutParams = {}) {
    this.qb.loading = true;
    const finalReturnURL =
      document.location.origin +
      encodeURI(
        '/auth-msg?message=' +
          (message == undefined ? 'Successfully Logged Out' : message) +
          '&logged_out=true&redirect=' +
          (returnTo ?? '/')
      );
    this.auth.logout({
      logoutParams: {
        // returnTo: document.location.origin,
        returnTo: finalReturnURL,
        client_id: environment.auth0ClientId,
        ...logoutParams,
      },
    });
  }

  logOutOfSSODomains() {
    const domains = environment.ssoDomains;
    domains.forEach(domain => {
      const logoutIframe = document.createElement('iframe');
      logoutIframe.style.position = 'absolute';
      logoutIframe.style.width = '0';
      logoutIframe.style.height = '0';
      logoutIframe.style.border = '0';
      logoutIframe.setAttribute('src', domain + '/logout');
      document.body.appendChild(logoutIframe);
    });
  }

  setUserLoggedIn(user: Auth0User) {
    if (user) {
      if (!user.email_verified) {
        if (user.resetPassword && user.resetPassword != '') {
          window.location.href = user.resetPassword;
        }
      }

      const phone = user.contactInfo.phone?.split(' x ') ?? [''];
      const fullName = user.contactInfo.fullName;

      this.user = {
        email: user.contactInfo.email ?? user.contactInfo.fullName,
        azureOid: user.sub,
        firstName: user.contactInfo.firstName,
        lastName: user.contactInfo.lastName,
        fullName: fullName,
        agencyToken: user.agencyToken,
        agencyName: user.agencyName,
        isFullService: user.isFullService,
        userId: user.dbAppUserId,
        isVendor: user.isVendor,
        isAgency: user.isAgency,
        isColbi: user.isColbi,
        isQbat: user.isQbat,
        roleId: user.qbRoleId,
        companyName: user.contactInfo.companyName,
        isConfirmed: user.email_verified,
        phone: phone[0],
        extension: phone.length > 1 ? phone[1] : '',
        baseAccess: this.readBaseRoleAccess(user),
      } as any;
    } else {
      this.user = undefined;
    }

    // Check for redirect parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const redirectPath = urlParams.get('redirect');

    if (redirectPath) {
      // If we have a redirect path, navigate there
      this.router.navigate([redirectPath]).then(() => {
        this.qb.loading = false;
      });
    } else if (window.location.pathname === '/') {
      // Only navigate to home if we're on the root path and no redirect specified
      this.navigateHome().then(() => {
        this.qb.loading = false;
      });
    } else {
      this.qb.loading = false;
    }

    return !!this.user;
  }

  // Navigates to the homepage depending on the type of user. Returns a promise that is resolved upon navigation complete.
  navigateHome(): Promise<boolean> {
    let navigatePath = ['/'];
    if (this.isAgency) {
      navigatePath = ['agency'];
    }
    if (this.isVendor) {
      navigatePath = ['vendor'];
    }
    if (this.isQbat) {
      navigatePath = ['qbat'];
    }
    return this.router.navigate(navigatePath);
  }

  canAccessFeature(feature: string | string[]): boolean {
    if (environment.site === 'lausd') return true;
    const access = this.user?.baseAccess as Record<string, boolean> | undefined;
    if (typeof feature === 'string') {
      return !!access?.[feature];
    }
    if (Array.isArray(feature)) {
      return feature.some(val => !!access?.[val]);
    }
    return false;
  }

  private readBaseRoleAccess(user: Auth0User): BaseRoleAccess {
    const claim = user?.baseRoleAccess as BaseRoleAccess | string | undefined;
    if (!claim) return {};
    if (typeof claim === 'string') {
      try {
        return JSON.parse(claim) as BaseRoleAccess;
      } catch {
        return {};
      }
    }
    return claim;
  }

  hasPermission(permission: string): Observable<boolean> {
    if (!this._storedPermissions) {
      return this.permissions.pipe(
        map(p => p.filter(p => p.includes(permission))),
        map(p => p.length > 0)
      );
    }
    return of(this._storedPermissions.includes(permission));
  }
  hasRole(role: string): Observable<boolean> {
    if (!this._storedRoles) {
      return this.roles.pipe(
        map(r => r.filter(r => r.includes(role))),
        map(r => r.length > 0)
      );
    }
    return of(this._storedRoles.includes(role));
  }
}
